import React, { useMemo } from 'react';
import type { PersonalReportSummaryJson } from '../../data/mockPersonalReportSummary';
import { buildMultiViewProfile } from './multiViewProfile';
import {
  competencyBand,
  competencyMeetsRequirement,
  consistencyFromRange,
  consistencyTier,
  isHighPotential,
  nineBoxLabel,
} from './shared';

type MetricCardProps = {
  id?: string;
  title: string;
  value: React.ReactNode;
  status?: React.ReactNode;
  meta?: React.ReactNode;
  empty?: boolean;
};

function MetricCard({ id, title, value, status, meta, empty }: MetricCardProps) {
  return (
    <article
      id={id}
      className={`report-metric-card${empty ? ' is-empty' : ''}`}
    >
      <div className="report-metric-title">{title}</div>
      <div className="report-metric-value">{value}</div>
      <div className="report-metric-status">{status ?? '—'}</div>
      <div className="report-metric-meta">{meta ?? null}</div>
    </article>
  );
}

/** 迷你一致度环：仅作底部次要信息 */
function MiniConsistencyRing({ pct, size = 28 }: { pct: number; size?: number }) {
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct / 100);

  return (
    <span
      className="report-metric-mini-ring"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ai-accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </span>
  );
}

function parseCompetencyBand(band: string): { label: string; note: string | null } {
  const m = band.match(/^(.+?)（(.+?)）$/);
  if (m) return { label: m[1], note: `（${m[2]}）` };
  return { label: band, note: null };
}

function talentStatusLabel(cell: string): string {
  const full = nineBoxLabel(cell) ?? cell;
  return full.replace(new RegExp(`^${cell}`, 'i'), '') || full;
}

function perfStatusLabel(level: string | null): string {
  if (!level) return '—';
  const key = level.toUpperCase();
  if (key === 'H') return '高档';
  if (key === 'M') return '中档';
  if (key === 'L') return '低档';
  return level;
}

function QualityMetricCard({
  evaluationBreakdown,
  multiViewProfile: multiViewProfileProp,
}: {
  evaluationBreakdown: PersonalReportSummaryJson['evaluationBreakdown'];
  multiViewProfile?: ReturnType<typeof buildMultiViewProfile>;
}) {
  const rows = evaluationBreakdown ?? [];
  const multiViewProfile = useMemo(
    () => multiViewProfileProp ?? buildMultiViewProfile(rows),
    [multiViewProfileProp, rows],
  );
  const insufficient = rows.length < 2;

  const consistencyMeta = useMemo(() => {
    if (!multiViewProfile) return null;
    const { range, fullScore, globalAvg } = multiViewProfile.meta;
    return {
      pct: consistencyFromRange(range, fullScore),
      tier: consistencyTier(range),
      globalAvg,
    };
  }, [multiViewProfile]);

  if (insufficient || !consistencyMeta || !Number.isFinite(consistencyMeta.globalAvg)) {
    return (
      <MetricCard
        id="evaluation"
        title="素质项评价"
        value="暂无数据"
        status="—"
        meta={
          insufficient ? '评价人数量不足，暂无法生成一致性分析。' : '暂无一致性分析数据。'
        }
        empty
      />
    );
  }

  return (
    <MetricCard
      id="evaluation"
      title="素质项评价"
      value={consistencyMeta.globalAvg.toFixed(2)}
      status="全员均分 · 标尺 0～7"
      meta={
        <div className="report-metric-meta-row">
          <MiniConsistencyRing pct={consistencyMeta.pct} />
          <span>
            评价一致度 {consistencyMeta.pct.toFixed(0)}% · {consistencyMeta.tier.label}
          </span>
        </div>
      }
    />
  );
}

export function MetricOverview({
  data,
  effectiveRadar: _effectiveRadar,
  multiViewProfile,
}: {
  data: PersonalReportSummaryJson;
  effectiveRadar: PersonalReportSummaryJson['radar'];
  multiViewProfile?: ReturnType<typeof buildMultiViewProfile>;
}) {
  const { emp, performanceScore, evaluationBreakdown } = data;
  const nb = emp.nineBoxResults?.[0];
  const competency = data.competencyAssessment ?? emp.competencyAssessments?.[0];
  const hp = emp.highPotentialAsEvaluatee?.[0];
  const compScore =
    competency?.weightedScore != null ? Number(competency.weightedScore) : null;
  const finalCell = nb?.finalCell != null ? String(nb.finalCell) : null;
  const perfScore = performanceScore?.score;
  const perfLevel =
    nb?.performanceLevel != null
      ? String(nb.performanceLevel)
      : performanceScore?.level != null
        ? String(performanceScore.level)
        : null;
  const hpLabel = hp?.resultLabel != null ? String(hp.resultLabel) : null;
  const hpFlag = isHighPotential(hpLabel);

  const cards: MetricCardProps[] = [
    perfScore != null
      ? {
          title: '绩效表现',
          value: (
            <>
              {String(perfScore)}
              {perfLevel ? (
                <span className="report-metric-value-paren"> ({perfLevel})</span>
              ) : null}
            </>
          ),
          status: perfStatusLabel(perfLevel),
          meta: '本期绩效综合得分，结合九宫格横轴定位。',
        }
      : {
          title: '绩效表现',
          value: '暂无数据',
          status: '—',
          meta: '本期尚未录入绩效综合分。',
          empty: true,
        },
    hpLabel
      ? {
          title: '高潜识别',
          value: (
            <span className={hpLabel.length > 6 ? 'report-metric-value--compact' : undefined}>
              {hpLabel}
            </span>
          ),
          status:
            hpFlag === true ? '高潜' : hpFlag === false ? '非高潜' : /观察/.test(hpLabel) ? '观察中' : '综合判断',
          meta:
            hpFlag === true
              ? '建议纳入加速池，绑定关键事件复盘。'
              : hpFlag === false
                ? '当前以稳健提升为主，待短板补齐后复评。'
                : '结合九宫格与胜任力证据综合判断。',
        }
      : {
          title: '高潜识别',
          value: '暂无数据',
          status: '—',
          meta: '本期尚未完成高潜识别评估。',
          empty: true,
        },
    (() => {
      if (compScore == null) {
        return {
          title: '岗位胜任力',
          value: '暂无数据',
          status: '—',
          meta: '本期尚未收到岗位胜任力评估。',
          empty: true,
        } satisfies MetricCardProps;
      }
      const band = parseCompetencyBand(competencyBand(compScore));
      return {
        title: '岗位胜任力',
        value: (
          <>
            {compScore.toFixed(2)}
            <span className="report-metric-value-suffix"> / 5</span>
          </>
        ),
        status: (
          <>
            {band.label}
            {band.note ? (
              <span className="report-metric-status-note">{band.note}</span>
            ) : null}
          </>
        ),
        meta:
          competencyMeetsRequirement(compScore) === true
            ? '当前加权分达到岗位要求基线。'
            : competencyMeetsRequirement(compScore) === false
              ? '加权分低于合格线，需优先补齐岗位短板。'
              : '—',
      } satisfies MetricCardProps;
    })(),
    finalCell
      ? {
          title: '人才定位',
          value: finalCell,
          status: talentStatusLabel(finalCell),
          meta: '纵轴素质 × 横轴绩效的综合落位。',
        }
      : {
          title: '人才定位',
          value: '暂无数据',
          status: '—',
          meta: '本期尚未完成九宫格定档。',
          empty: true,
        },
  ];

  return (
    <section id="metrics" className="report-metric-overview" aria-label="核心指标概览">
      <QualityMetricCard
        evaluationBreakdown={evaluationBreakdown}
        multiViewProfile={multiViewProfile}
      />
      {cards.map((card) => (
        <MetricCard key={card.title} {...card} />
      ))}
    </section>
  );
}
